import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export function KpiCard({ label, value, icon: Icon, sub, accent = false, testid, delay = 0 }) {
  return (
    <Card
      data-testid={testid}
      style={{ animationDelay: `${delay}ms` }}
      className={`animate-fade-up p-6 border transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${
        accent ? "bg-primary text-primary-foreground border-primary" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className={`text-xs uppercase tracking-[0.1em] ${accent ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          {label}
        </p>
        {Icon && (
          <Icon
            className={`h-[18px] w-[18px] ${accent ? "text-primary-foreground/80" : "text-muted-foreground"}`}
            strokeWidth={2}
          />
        )}
      </div>
      <p className="font-head text-3xl md:text-[32px] font-bold tracking-tight tabular-nums mt-3">{value}</p>
      {sub && (
        <p className={`text-xs mt-1.5 ${accent ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{sub}</p>
      )}
    </Card>
  );
}

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/20",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/20",
  rejected: "bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/20",
  open: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/20",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/20",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/20",
  processing: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/20",
  shipped: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/20",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/20",
  cancelled: "bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/20",
};

export function StatusBadge({ status }) {
  const label = String(status || "").replace(/_/g, " ");
  return (
    <Badge
      variant="outline"
      className={`capitalize font-medium ${STATUS_STYLES[status] || "bg-muted text-muted-foreground"}`}
      data-testid={`status-${status}`}
    >
      {label}
    </Badge>
  );
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24 text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, desc, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-xl bg-card/50">
      {Icon && <Icon className="h-10 w-10 text-muted-foreground/50 mb-3" strokeWidth={1.5} />}
      <p className="font-head font-semibold text-lg">{title}</p>
      {desc && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{desc}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(160 25% 45%)",
  "hsl(35 55% 45%)",
];
